import { useState, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import FailureErrorImage from "assets/failure.svg";
import ButtonLink from "core/components/ButtonLink";
import { Row } from "core/styles";
import { ERROR_TYPES, REVERSE_ERROR_TYPES } from "core/constants";
import {
  Panel,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  PanelContent,
} from "core/components/Panel";
import useMountEffect from "core/hooks/useMountEffect";

const FailureError = ({ resetError, type, error }) => {
  const auth = useAuth();

  const buttonText =
    type === ERROR_TYPES.AUTH_ERROR ? "Back to Login" : "Return to Home";

  const [showDebugButton, setShowDebugButton] = useState(false);
  const [showErrorTrace, setShowErrorTrace] = useState(false);

  useMountEffect(() => {
    const handleKeyDown = (event) => {
      // Ignore if user is typing in input/textarea/contenteditable
      const tag = event.target.tagName;
      const isTyping =
        tag === "INPUT" || tag === "TEXTAREA" || event.target.isContentEditable;

      if (isTyping) return;

      const cmdOrCtrl = event.metaKey || event.ctrlKey;

      if (cmdOrCtrl && event.shiftKey && event.key.toLowerCase() === "d") {
        event.preventDefault();
        setShowDebugButton((prev) => !prev);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  });

  const handlePrimaryAction = async () => {
    if (type === ERROR_TYPES.AUTH_ERROR) {
      resetError?.();
      await auth.removeUser();
      await auth.signinRedirect();
    } else {
      window.location.replace("/");
    }
  };

  return (
    <div
      data-test="failure-error-page"
      style={{
        height: "100%",
        width: "80%",
        margin: "0px auto",
        padding: "17vh 0px 15vh",
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        gap: "24px",
      }}
    >
      <img
        src={FailureErrorImage}
        alt="failure error page"
        style={{
          display: "block",
          width: "455px",
          margin: "0px auto",
        }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <span
          style={{
            textAlign: "center",
            fontSize: "40px",
            fontWeight: "700",
            color: "var(--color-base-content)",
            maxWidth: "854px",
          }}
        >
          Something went wrong
        </span>

        <span
          style={{
            textAlign: "center",
            fontSize: "32px",
            fontWeight: "400",
            color: "var(--color-base-content)",
            maxWidth: "854px",
          }}
        >
          Well, that didn&apos;t go as planned. Please{" "}
          {[
            ERROR_TYPES.AUTH_ERROR,
            ERROR_TYPES.APPLICATION_ERROR,
            ERROR_TYPES.PAGE_ERROR,
          ].includes(type) && "try to log back in or "}
          contact support if the issue persists.
        </span>
      </div>

      <Row>
        {[
          ERROR_TYPES.AUTH_ERROR,
          ERROR_TYPES.APPLICATION_ERROR,
          ERROR_TYPES.PAGE_ERROR,
        ].includes(type) && (
          <ButtonLink variant="outlined" to="/" onClick={handlePrimaryAction}>
            {buttonText}
          </ButtonLink>
        )}
        <ButtonLink href="https://success.populi.ai/hc/en-us">
          Contact Support
        </ButtonLink>
        {showDebugButton && (
          <ButtonLink
            variant="outlined"
            onClick={() => setShowErrorTrace(true)}
          >
            Show Error Stack Trace
          </ButtonLink>
        )}
        <Panel
          isOpen={showErrorTrace}
          onOpenChange={(isOpen) => setShowErrorTrace(isOpen)}
          //   onCloseRedirectTo="/"
        >
          <PanelHeader>
            <PanelTitle>Error Stack Trace</PanelTitle>
            <PanelDescription></PanelDescription>
          </PanelHeader>
          <PanelContent>
            <div>
              Identified Error Type:{" "}
              {REVERSE_ERROR_TYPES[type] || "Unknown Error Type"}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              {error?.stack || "No stack trace available"}
            </div>
          </PanelContent>
        </Panel>
      </Row>
    </div>
  );
};

export default FailureError;
